import { Fragment, MouseEvent, useEffect } from 'react';
import { Link, useLocation } from 'react-router';
import {
    adminApprovals,
    adminBackgroundChecks,
    adminBackoffice,
    adminCandidateIM,
    adminCandidates,
    adminCandidatesInterviewed,
    adminCheckIns,
    adminContractorInvoices,
    adminContractors,
    adminCustomerExpenses,
    adminCustomerInvoices,
    adminCustomerPositions,
    adminCustomers,
    adminDeposits,
    adminEquipment,
    adminHolidays,
    adminIMTemplates,
    adminInvoiceLines,
    adminOfferLetters,
    adminOnboarding,
    adminRevenue,
    adminTalentLeads,
    customerCompanyView,
    customerContractors,
    customerDeposits,
    customerDocuments,
    customerEquipment,
    customerEquipmentApprove,
    customerExpenses,
    customerInvoices,
    customerPositions,
    customerRefer,
    customerTimeOff,
} from '~/acl';
import logoImg from '~/assets/icons/logo-full.svg';
import { useAuthData } from '~/auth';
import { AuthUser } from '~/auth/user.ts';
import { isIncompleteProfileBuilder } from '~/candidates/incompleteProfile';
import { Logo } from '~/components/Company';
import QuickNavTrigger from '~/components/QuickNav/QuickNavTrigger';
import Button, { ButtonLink } from '~/components/new/Button';
import Icon, { IconVariants, RemixIconNames } from '~/components/ui/Icon';
import { RemotelyJobOpeningID } from '~/types/domain';
import { isStaging } from '~/utils/env';
import {
    AI_PROFILE_BUILDER,
    BACKGROUND_CHECKS,
    DEPOSITS_CUSTOMER,
    isEnabled,
} from '~/utils/flags.ts';
import { zeroPad } from '~/utils/format';
import { classNames } from '~/utils/styles';
import { ImpersonateForm } from './UserMenu';

type SidebarProps = {
    open: boolean;
    mobileOnly?: boolean;
    onClose: () => void;
    user: AuthUser;
};

export default function SideBar({ user, open, mobileOnly = false, onClose }: SidebarProps) {
    const handleContainerClose = (e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();

        if ((e.target as HTMLElement).closest('.sidebar-container a')) {
            onClose();
        }
    };

    return (
        <aside
            className={classNames({
                sidebar: true,
                'sidebar-mobile-only': mobileOnly,
                'sidebar-open': open,
                'sidebar-staging': isStaging(),
            })}
            onClick={onClose}
            data-test-component='layout/SideBar'>
            <div
                className='sidebar-container'
                onClick={handleContainerClose}>
                <div className='sidebar-brand large-only'>
                    <img
                        src={logoImg}
                        alt='Remotely'
                        data-test-component='layout/SideBar/Logo'
                    />
                    <QuickNavTrigger />
                </div>

                <div className='sidebar-menu small-or-medium-only'>
                    <h2>Menu</h2>
                    <Button
                        icon={RemixIconNames.RiCloseLargeLine}
                        variant='floating'
                        title='Close menu'
                        onClick={onClose}
                        data-test-component='layout/SideBar/CloseMenu'
                    />
                </div>

                <Navigation user={user} />

                {!user.isExContractor() && <Refer />}
            </div>
        </aside>
    );
}

function Navigation({ user }: { user: AuthUser }) {
    const displayTitles = hasMultipleCategoryRoles(user);
    const openPositionsMenu = (
        <Item
            icon={RemixIconNames.RiSuitcaseLine}
            activeIcon={RemixIconNames.RiSuitcaseFill}
            title='Job Positions'
            path='/job_positions'
            submenu={
                user.hasAccess(adminCandidates)
                    ? {
                          Positions: {
                              path: '/job_positions/list',
                          },
                          Applications: {
                              path: '/job_positions/applications',
                          },
                          'Remotely Pipeline': {
                              path: `/job_positions/${RemotelyJobOpeningID}/in_process/list`,
                          },
                      }
                    : undefined
            }
        />
    );

    const checkOverflow = () => {
        const sidebar = document.querySelector('.sidebar-navigation');
        if (!sidebar) return;
        const isOverflowing = sidebar.scrollHeight > sidebar.clientHeight;
        sidebar.classList.toggle('overflowing', isOverflowing);
    };

    useEffect(() => {
        window.addEventListener('resize', checkOverflow);
        checkOverflow();
        return () => {
            window.removeEventListener('resize', checkOverflow);
        };
    }, []);

    return (
        <nav
            className='sidebar-navigation'
            data-test-component='layout/SideBar/Navigation'>
            {(user.isContractor() || user.isCandidate() || user.isRemotely()) && (
                <Fragment>
                    {displayTitles && <h4>Me</h4>}
                    <ul
                        data-test-component='layout/SideBar/Navigation/Section'
                        data-test-name='shared'>
                        <Item
                            icon={RemixIconNames.RiHomeLine}
                            activeIcon={RemixIconNames.RiHomeFill}
                            title='Home'
                            path='/home'
                        />
                        {(user.isContractor() || user.hasInvoices()) && (
                            <Item
                                icon={RemixIconNames.RiBillLine}
                                activeIcon={RemixIconNames.RiBillFill}
                                title='Invoices'
                                path='/invoices'
                            />
                        )}
                        {user.isContractor() && (
                            <Item
                                icon={RemixIconNames.RiMoneyDollarBoxLine}
                                activeIcon={RemixIconNames.RiMoneyDollarBoxFill}
                                title='Expenses'
                                path='/expenses'
                            />
                        )}
                        {user.isContractor() && user.hasBenefit('pto') && (
                            <Item
                                icon={RemixIconNames.RiCalendarLine}
                                activeIcon={RemixIconNames.RiCalendarFill}
                                title='Time Off'
                                path={
                                    user.hasContract() ? '/timeoff/calendar' : '/timeoff/requests'
                                }
                                alternativePaths={['/timeoff']}
                            />
                        )}
                        {(user.isCandidate() || user.isContractor() || user.isRemotely()) &&
                            (!user.isUsBased() || user.isRemotely()) &&
                            !user.isTalentFreelancer() &&
                            !user.isExContractor() && (
                                <Item
                                    icon={RemixIconNames.RiSuitcaseLine}
                                    activeIcon={RemixIconNames.RiSuitcaseFill}
                                    title='Job Opportunities'
                                    path='/opportunities'
                                    alternativePaths={['/position/', '/company/']}
                                />
                            )}
                        {(user.isCandidate() || user.isContractor()) &&
                            !user.isExContractor() &&
                            (isEnabled(AI_PROFILE_BUILDER)
                                ? user.profile_complete || isIncompleteProfileBuilder(user)
                                : true) && (
                                <Item
                                    icon={RemixIconNames.RiSendPlaneLine}
                                    activeIcon={RemixIconNames.RiSendPlaneFill}
                                    title='Job Applications'
                                    path='/applications'
                                />
                            )}
                        {user.isRemotely() && (
                            <Item
                                icon={RemixIconNames.RiTaskLine}
                                activeIcon={RemixIconNames.RiTaskFill}
                                title='Tasks'
                                path='/tasks'
                            />
                        )}
                    </ul>
                </Fragment>
            )}

            {user.isCustomer() && (
                <Fragment>
                    {(displayTitles || user.hasAccess(customerCompanyView)) && (
                        <CompanyHeader
                            user={user}
                            multipleRoles={displayTitles}
                        />
                    )}
                    <ul
                        data-test-component='layout/SideBar/Navigation/Section'
                        data-test-name='customer'>
                        {!displayTitles && (
                            <Item
                                icon={RemixIconNames.RiHomeLine}
                                activeIcon={RemixIconNames.RiHomeFill}
                                title='Home'
                                path='/home'
                            />
                        )}
                        {user.hasAccess(customerContractors) && (
                            <Item
                                icon={RemixIconNames.RiUser2Line}
                                activeIcon={RemixIconNames.RiUser2Fill}
                                title='Contractors'
                                path='/customer/contractors'
                            />
                        )}
                        {user.hasAccess(customerPositions) && openPositionsMenu}
                        {user.hasAccess(customerInvoices) && (
                            <Item
                                icon={RemixIconNames.RiBillLine}
                                activeIcon={RemixIconNames.RiBillFill}
                                title='Invoices'
                                path='/customer/invoices'
                            />
                        )}
                        {user.hasAccess(customerDeposits) && isEnabled(DEPOSITS_CUSTOMER) && (
                            <Item
                                icon={RemixIconNames.RiArchiveDrawerLine}
                                activeIcon={RemixIconNames.RiArchiveDrawerFill}
                                title='Deposits'
                                path='/customer/deposits'
                            />
                        )}
                        {user.hasAccess(customerExpenses) && (
                            <Item
                                icon={RemixIconNames.RiMoneyDollarBoxLine}
                                activeIcon={RemixIconNames.RiMoneyDollarBoxFill}
                                title='Expenses'
                                path='/customer/expenses'
                            />
                        )}
                        {user.hasAccess(customerTimeOff) && (
                            <Item
                                icon={RemixIconNames.RiCalendarLine}
                                activeIcon={RemixIconNames.RiCalendarFill}
                                title='Time Off'
                                path='/customer/timeoff/calendar'
                                alternativePaths={['/customer/timeoff']}
                            />
                        )}
                        {user.hasAccess(customerEquipment) ? (
                            <Item
                                icon={RemixIconNames.RiMacbookLine}
                                activeIcon={RemixIconNames.RiMacbookFill}
                                title='Equipment'
                                path='/customer/equipment'
                                submenu={{
                                    Quotes: {
                                        path: '/customer/equipment/quotes',
                                    },
                                }}
                            />
                        ) : (
                            user.hasAccess(customerEquipmentApprove) && (
                                <Item
                                    icon={RemixIconNames.RiMacbookLine}
                                    activeIcon={RemixIconNames.RiMacbookFill}
                                    title='Equipment Quotes'
                                    path='/customer/equipment/quotes'
                                />
                            )
                        )}
                        {user.hasAccess(customerDocuments) && (
                            <Item
                                icon={RemixIconNames.RiFile2Line}
                                activeIcon={RemixIconNames.RiFile2Fill}
                                title='Documents'
                                path='/customer/documents'
                            />
                        )}
                    </ul>
                </Fragment>
            )}

            {!user.isCustomer() && user.isRemotely() && (
                <ul
                    data-test-component='layout/SideBar/Navigation/Section'
                    data-test-name='remotely'>
                    {user.hasAccess(adminCustomerPositions) && openPositionsMenu}
                    {!user.hasAccess(adminCustomerPositions) && !user.isTalentFreelancer() && (
                        <Item
                            icon={RemixIconNames.RiSuitcaseLine}
                            activeIcon={RemixIconNames.RiSuitcaseFill}
                            title='Remotely Pipeline'
                            path={`/job_positions`}
                            submenu={
                                user.hasAccess(adminCandidates)
                                    ? {
                                          Applications: {
                                              path: '/job_positions/applications',
                                          },
                                      }
                                    : undefined
                            }
                        />
                    )}
                </ul>
            )}

            {user.isRemotely() && (
                <Fragment>
                    <h4>Admin</h4>
                    <ul
                        data-test-component='layout/SideBar/Navigation/Section'
                        data-test-name='admin'>
                        {user.hasAccess(adminCustomers) && (
                            <Item
                                icon={RemixIconNames.RiBuilding2Line}
                                activeIcon={RemixIconNames.RiBuilding2Fill}
                                title='Customers'
                                path='/admin/customers'
                                exclude={[
                                    '/admin/customers/invoices',
                                    '/admin/customers/deposits',
                                    '/admin/customers/expenses',
                                ]}
                                submenu={{
                                    'All customers': {
                                        path: '/admin/customers',
                                        exact: true,
                                    },
                                    'Sales Opportunities': {
                                        path: '/admin/customers/applications',
                                        exact: true,
                                    },
                                }}
                            />
                        )}
                        {user.hasAccess(adminContractors) && (
                            <Item
                                icon={RemixIconNames.RiUser2Line}
                                activeIcon={RemixIconNames.RiUser2Fill}
                                title='Contractors'
                                path='/admin/contractors'
                                exclude={[
                                    '/admin/contractors/invoices',
                                    '/admin/contractors/onboarding',
                                ]}
                                submenu={{
                                    'All contractors': {
                                        path: '/admin/contractors',
                                        exact: true,
                                    },
                                    'New contractors': {
                                        path: adminContractorPaths.hires,
                                        exact: true,
                                    },
                                    Starting: {
                                        path: adminContractorPaths.starting,
                                        exact: true,
                                    },
                                    Terminations: {
                                        path: adminContractorPaths.terminations,
                                        exact: true,
                                    },
                                }}
                            />
                        )}
                        {user.hasAccess(adminCandidates, adminCandidatesInterviewed) && (
                            <>
                                <Item
                                    icon={RemixIconNames.RiUserFollowLine}
                                    activeIcon={RemixIconNames.RiUserFollowFill}
                                    title='Candidates'
                                    path='/admin/candidates'
                                    alternativePaths={['/candidates']}
                                />

                                <Item
                                    icon={RemixIconNames.RiUserSharedLine}
                                    activeIcon={RemixIconNames.RiUserSharedFill}
                                    title='Applicants'
                                    path='/admin/applicants'
                                />
                            </>
                        )}
                        {user.hasAccess(adminTalentLeads) && (
                            <Item
                                icon={RemixIconNames.RiUserSearchLine}
                                activeIcon={RemixIconNames.RiUserSearchFill}
                                title='Talent Leads'
                                path='/admin/talent-leads'
                            />
                        )}
                        {user.hasAccess(adminCandidates) && (
                            <Item
                                icon={RemixIconNames.RiMegaphoneLine}
                                activeIcon={RemixIconNames.RiMegaphoneFill}
                                title='Referrals'
                                path='/admin/referrals'
                            />
                        )}
                        {user.hasAccess(adminDeposits) && (
                            <Item
                                icon={RemixIconNames.RiArchiveDrawerLine}
                                activeIcon={RemixIconNames.RiArchiveDrawerFill}
                                title='Deposits'
                                path='/admin/customers/deposits'
                                submenu={{
                                    'By contractor': {
                                        path: '/admin/customers/deposits/contractors',
                                        exact: true,
                                    },
                                    'By customer': {
                                        path: '/admin/customers/deposits/balance',
                                        exact: true,
                                    },
                                }}
                            />
                        )}
                        {user.hasAccess(adminCustomerExpenses) && (
                            <Item
                                icon={RemixIconNames.RiMoneyDollarBoxLine}
                                activeIcon={RemixIconNames.RiMoneyDollarBoxFill}
                                title='Expenses'
                                path='/admin/customers/expenses'
                            />
                        )}
                        {user.hasAccess(adminEquipment) && (
                            <Item
                                icon={RemixIconNames.RiMacbookLine}
                                activeIcon={RemixIconNames.RiMacbookFill}
                                title='Equipment'
                                path='/admin/equipment'
                                submenu={{
                                    Quotes: {
                                        path: '/admin/equipment/quotes',
                                    },
                                }}
                            />
                        )}
                        {user.hasAccess(adminOnboarding) && (
                            <Item
                                icon={RemixIconNames.RiShipLine}
                                activeIcon={RemixIconNames.RiShipFill}
                                title='Onboarding'
                                path='/admin/contractors/onboarding'
                            />
                        )}
                        {user.hasAccess(adminContractorInvoices) && (
                            <Item
                                icon={RemixIconNames.RiBillLine}
                                activeIcon={RemixIconNames.RiBillFill}
                                title='Contractor invoices'
                                path='/admin/contractors/invoices'
                            />
                        )}
                        {user.hasAccess(adminCustomerInvoices) && (
                            <Item
                                icon={RemixIconNames.RiFileChartLine}
                                activeIcon={RemixIconNames.RiFileChartFill}
                                title='Customer invoices'
                                path='/admin/customers/invoices'
                                submenu={{
                                    Invoices: {
                                        path: '/admin/customers/invoices',
                                        exact: true,
                                    },
                                    'Due invoices': {
                                        path: '/admin/customers/invoices/due',
                                    },
                                }}
                            />
                        )}
                        {user.hasAccess(adminInvoiceLines) && (
                            <Item
                                icon={RemixIconNames.RiAlignJustify}
                                activeIcon={RemixIconNames.RiAlignJustify}
                                title='Line items'
                                path='/admin/lines'
                            />
                        )}
                        {user.hasAccess(adminRevenue) && (
                            <Item
                                icon={RemixIconNames.RiMoneyDollarCircleLine}
                                activeIcon={RemixIconNames.RiMoneyDollarCircleFill}
                                title='Revenue'
                                path='/admin/revenue'
                                submenu={{
                                    'All Revenue': {
                                        path: '/admin/revenue',
                                        exclude: ['/admin/revenue/remotely-fee'],
                                    },
                                    'Remotely Fees': {
                                        path: '/admin/revenue/remotely-fee',
                                    },
                                }}
                            />
                        )}
                        {user.hasAccess(adminOfferLetters) && (
                            <Item
                                icon={RemixIconNames.RiArticleLine}
                                activeIcon={RemixIconNames.RiArticleFill}
                                title='Offer letters'
                                path='/admin/offer-letters'
                            />
                        )}
                        {user.hasAccess(adminCandidates) && (
                            <Item
                                icon={RemixIconNames.RiChatCheckLine}
                                activeIcon={RemixIconNames.RiChatCheckFill}
                                title='Reference Checks'
                                path='/admin/references'
                            />
                        )}
                        {user.hasAccess(adminCheckIns) && (
                            <Item
                                icon={RemixIconNames.RiQuestionAnswerLine}
                                activeIcon={RemixIconNames.RiQuestionAnswerFill}
                                title='Check-Ins'
                                path='/admin/checkins'
                            />
                        )}
                        {user.hasAccess(adminHolidays) && (
                            <Item
                                icon={RemixIconNames.RiCalendarEventLine}
                                activeIcon={RemixIconNames.RiCalendarEventFill}
                                title='Holidays'
                                path='/admin/holidays'
                            />
                        )}
                        {user.hasAccess(adminContractors) && (
                            <Item
                                icon={RemixIconNames.RiHealthBookLine}
                                activeIcon={RemixIconNames.RiHealthBookFill}
                                title='Health insurance'
                                path='/admin/insurance'
                            />
                        )}
                        {user.hasAccess(adminCandidates) && (
                            <Item
                                icon={RemixIconNames.RiCustomerServiceLine}
                                activeIcon={RemixIconNames.RiCustomerServiceFill}
                                title='AI Agents Calls'
                                path='/admin/profile-builder-calls'
                            />
                        )}
                        {user.hasAccess(adminBackgroundChecks) && isEnabled(BACKGROUND_CHECKS) && (
                            <Item
                                icon={RemixIconNames.RiShieldCheckLine}
                                activeIcon={RemixIconNames.RiShieldCheckFill}
                                title='Background checks'
                                path='/admin/background-checks'
                            />
                        )}
                        {user.hasAccess(adminCandidateIM) && (
                            <Item
                                icon={RemixIconNames.RiWhatsappLine}
                                activeIcon={RemixIconNames.RiWhatsappFill}
                                title='WhatsApp threads'
                                path='/admin/im/threads'
                            />
                        )}
                        {user.hasAccess(adminIMTemplates) && (
                            <Item
                                icon={RemixIconNames.RiLayout5Line}
                                activeIcon={RemixIconNames.RiLayout5Fill}
                                title='WhatsApp Templates'
                                path='/admin/im/templates'
                            />
                        )}
                        {user.hasAccess(adminApprovals) && (
                            <Item
                                icon={RemixIconNames.RiCheckboxCircleLine}
                                activeIcon={RemixIconNames.RiCheckboxCircleFill}
                                title='Approvals'
                                path='/admin/approvals'
                            />
                        )}
                        {user.hasAccess(adminBackoffice) && (
                            <Item
                                icon={RemixIconNames.RiPencilLine}
                                activeIcon={RemixIconNames.RiPencilFill}
                                title='Backoffice'
                                path='/admin/backoffice'
                            />
                        )}
                    </ul>
                </Fragment>
            )}

            <ul
                className='small-or-medium-only'
                data-test-component='layout/SideBar/Navigation/Section'
                data-test-name='user'>
                {(user.isContractor() || user.isCandidate()) && (
                    <Item
                        icon={RemixIconNames.RiUserLine}
                        activeIcon={RemixIconNames.RiUserFill}
                        title='Profile'
                        path='/profile'
                    />
                )}
                {user.isRemotely() && (
                    <Item
                        icon={RemixIconNames.RiSettingsLine}
                        activeIcon={RemixIconNames.RiSettingsFill}
                        title='Settings'
                        path='/settings'
                    />
                )}
                <Item
                    icon={RemixIconNames.RiLogoutBoxRLine}
                    activeIcon={RemixIconNames.RiLogoutBoxRFill}
                    title='Sign out'
                    path='/logout'
                    variant='danger'
                />
            </ul>

            <ImpersonateForm
                menuPlacement='top'
                className='small-or-medium-only'
            />
        </nav>
    );
}

function Item({
    path,
    title,
    icon,
    activeIcon,
    submenu,
    exclude,
    alternativePaths = [],
    variant = null,
}: {
    path: string;
    title: string;
    icon: keyof typeof RemixIconNames;
    activeIcon: keyof typeof RemixIconNames;
    submenu?: { [_: string]: { path: string; exact?: boolean; exclude?: string[] } };
    exclude?: string[];
    alternativePaths?: string[];
    variant?: IconVariants | null;
}) {
    const { pathname } = useLocation();

    let active = [path, ...alternativePaths].some((path) => {
        if (path === '/') {
            return pathname === path;
        }
        return pathname.startsWith(path);
    });

    if (active && exclude?.find((excluded) => pathname.startsWith(excluded))) {
        active = false;
    }

    return (
        <Fragment>
            <li
                className={
                    (active ? 'active sidebar-navigation-item-' : 'sidebar-navigation-item-') +
                    variant
                }
                data-test-component='layout/SideBar/Item'
                data-test-selected={active}>
                <Link to={path}>
                    <Icon
                        icon={active ? activeIcon : icon}
                        variant={variant ?? (active ? 'primary' : 'inherit')}
                        size={16}
                    />
                    {title}
                </Link>
            </li>
            {active && submenu && (
                <ul className='submenu'>
                    {Object.keys(submenu).map((title) => (
                        <SubmenuItem
                            key={submenu[title].path}
                            title={title}
                            path={submenu[title]}
                            exclude={submenu[title].exclude}
                        />
                    ))}
                </ul>
            )}
        </Fragment>
    );
}

function SubmenuItem({
    path,
    title,
    exclude,
}: {
    path: { path: string; exact?: boolean };
    title: string;
    exclude?: string[];
}) {
    const { pathname, search } = useLocation();

    let active: boolean;
    if (path.exact) {
        active = `${pathname}${search}` === path.path;
    } else {
        active = pathname.startsWith(path.path);
    }

    if (active && exclude?.find((excluded) => pathname.startsWith(excluded))) {
        active = false;
    }

    return (
        <li
            className={active ? 'active' : ''}
            data-test-component='layout/SideBar/Item/SubMenuItem'
            data-test-selected={active}>
            <Link to={path.path}>{title}</Link>
        </li>
    );
}

function CompanyHeader({
    user,
    multipleRoles = false,
}: {
    user: AuthUser;
    multipleRoles?: boolean;
}) {
    return (
        <Link
            to='/customer/company'
            className={classNames({
                'sidebar-company-header': true,
                'sidebar-company-header-multi': multipleRoles,
            })}>
            <Logo
                url={user.company_logo}
                size={24}
                name={user.company}
            />
            <span className='sidebar-company-header-name'>{user.company}</span>
            <Icon
                icon={RemixIconNames.RiArrowRightSLine}
                colorVar='core-tertiary'
            />
        </Link>
    );
}

const REFER_TYPES = {
    developer: {
        button: 'Refer a developer',
        text: 'Earn more income by referring developers to Remotely',
        path: '/referrals',
    },
    company: {
        button: 'Refer a company',
        text: 'Refer a tech company and enjoy savings on Remotely fees together!',
        path: '/referrals/startup/refer',
    },
};

function Refer() {
    const { user } = useAuthData();
    let referType = null;
    if (user.isContractor() || user.isCandidate()) {
        referType = REFER_TYPES.developer;
    }

    if (user.hasAccess(customerRefer) && !user.isRemotely()) {
        referType = REFER_TYPES.company;
    }

    if (!referType) {
        return null;
    }

    return (
        <div className='sidebar-refer'>
            <p>{referType.text}</p>
            <ButtonLink
                icon={RemixIconNames.RiMegaphoneLine}
                to={referType.path}>
                {referType.button}
            </ButtonLink>
        </div>
    );
}

const now = new Date();
const startOfMonth = new Date(`${now.getUTCFullYear()}-${zeroPad(now.getUTCMonth() + 1)}-01`);
const endOfMonth = new Date(`${now.getUTCFullYear()}-${zeroPad(now.getUTCMonth() + 2)}-01`);

const adminContractorPaths = {
    terminations: pathWithFilters(
        '/admin/contractors',
        [
            { type: 'and', field: 'terminated_at', operator: 'bigger_equal', value: startOfMonth },
            { type: 'and', field: 'terminated_at', operator: 'smaller', value: endOfMonth },
        ],
        'terminations=true',
    ),
    starting: pathWithFilters('/admin/contractors', [
        { type: 'and', field: 'started_at', operator: 'bigger_equal', value: startOfMonth },
        { type: 'and', field: 'started_at', operator: 'smaller', value: endOfMonth },
    ]),
    hires: pathWithFilters('/admin/contractors', [
        { type: 'and', field: 'started_at', operator: 'bigger_equal', value: startOfMonth },
        { type: 'and', field: 'company_name', operator: 'not_like', value: 'Remotely' },
    ]),
};

function pathWithFilters(base: string, filters: any, ...rest: string[]) {
    return `${base}?filters=${encodeURIComponent(JSON.stringify(filters))}${
        rest.length > 0 ? '&' : ''
    }${rest.join('&')}`;
}

function hasMultipleCategoryRoles(user: AuthUser) {
    return (
        [
            user.isRemotely(),
            user.isCustomer(),
            user.isContractor() || user.isApplicant() || user.isCandidate(),
        ].filter((x) => x).length > 1
    );
}
